// Phase 39 (Corr-4 / PART C) — client Web Push arming. Called the moment the
// user first engages with calling WHILE PRESENT (opening the comms/phone
// panel): request notification permission in that gesture, then subscribe this
// device's PushManager and register the subscription server-side — so a LATER
// incoming call can wake this tab even when it's backgrounded/frozen/closed.
//
// Deliberately NOT on login (cold prompt → sticky auto-deny) and NOT on first
// incoming call (that one would arrive unarmed). If anything is unsupported,
// denied, or the prod VAPID key isn't set, it no-ops silently — the in-app card
// + Part-B durable re-present remain the fallback. Never nags.

import { api } from "../api";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

let _armed = false;
let _arming = false;

const pushSupported = () =>
  typeof globalThis.navigator !== "undefined" &&
  "serviceWorker" in globalThis.navigator &&
  typeof globalThis.window !== "undefined" &&
  "PushManager" in globalThis.window &&
  "Notification" in globalThis.window;

/* 208 §14 — ONE NOTIFICATION PER CALL, PER BROWSER. Two writers can show a
   call: the page itself while LexCRM is behind other windows (App.tsx
   `maybeRingNotification`, a page notification), and this browser's service
   worker when the server's ring push reaches it (public/sw.js `push`, a
   persistent notification). Chrome lists both, their shared tag
   notwithstanding (Laurent, prod, 24/09 — Safari showed one). [ADVISOR] Where
   this browser HOLDS a subscription the server delivers to, the push's banner
   is the one — it also reaches a frozen or closed tab — and the page posts
   none; where it holds none, the page posts its own, as before.

   HOW THE PAGE LEARNS IT: its service worker's subscription
   (`pushManager.getSubscription()`), made with the key the server pushes with
   (`/push/vapid-public-key`: no key, no push; another key, a push its service
   refuses) — and, at sign-in, before any ring, registered for THIS user (the
   upsert `armCallPush` makes): a subscription last registered by whoever used
   this browser before would carry that person's rings, not this one's. The
   notification permission is the push banner's too. Anything this browser
   cannot answer is « none », and the page posts its own. */
let _held: Promise<boolean> | null = null;

const sameBytes = (a: Uint8Array, b: Uint8Array) => a.length === b.length && a.every((x, i) => x === b[i]);

async function heldAndRegistered(): Promise<boolean> {
  if (!pushSupported() || Notification.permission !== "granted") return false;
  const reg = await globalThis.navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) return false;
  const r = (await api.get("/push/vapid-public-key")) as { key: string | null } | null;
  const key = r?.key;
  if (!key) return false;
  const own = sub.options.applicationServerKey;
  if (own && !sameBytes(new Uint8Array(own), urlBase64ToUint8Array(key))) return false;
  await api.post("/push/subscribe", { subscription: sub.toJSON() });
  return true;
}

/** 208 §14 — at sign-in: whether this browser holds a push subscription the server delivers to, registered for this user. */
export function primeCallPush(): Promise<boolean> {
  _held = heldAndRegistered().catch(() => false);
  return _held;
}

/** 208 §14 — does this browser hold one? What `primeCallPush` or an arming learned; nothing learned yet is « none ». */
export function callPushHeld(): Promise<boolean> {
  return _held ?? Promise.resolve(false);
}

// MUST be invoked synchronously inside a user gesture (the panel-open click) so
// Notification.requestPermission() is allowed; the checks before it are sync.
export async function armCallPush() {
  if (_armed || _arming) return;
  if (!pushSupported()) return;
  // Permission: ask once, in this gesture. Denied/blocked → silent fallback.
  if (Notification.permission === "denied") return;
  if (Notification.permission === "default") {
    let p;
    try { p = await Notification.requestPermission(); } catch { return; }
    if (p !== "granted") return;
  }
  _arming = true;
  try {
    // The prod VAPID public key (null until set → no push subsystem yet).
    let key: string | null = null;
    try {
      const r = (await api.get("/push/vapid-public-key")) as { key: string | null } | null;
      key = r && r.key;
    } catch {
      return;
    }
    if (!key) return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    }
    await api.post("/push/subscribe", { subscription: sub.toJSON() });
    _armed = true;
    _held = Promise.resolve(true); // 208 §14 — armed here: the push's banner is the one from now on
  } catch {
    /* best-effort — never surface */
  } finally {
    _arming = false;
  }
}
